import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { useAppStore } from '../../../shared/hooks/useAppStore';
import { useVmFeatures } from '../../../shared/hooks/useVmFeatures';
import { debug } from '../../../shared/lib/general';
import { getOrderInfo, ORDER_QUERY_KEYS } from '../api/order.api';
import { RECEIPT_POLL_INTERVAL_MS, shouldPollOrder } from '../lib/receipt-state';

interface ReceiptPolling {
    /** The receipt was in flight when the screen opened — the only case that polls. */
    wasInFlight: boolean;
}

interface NextReceiptPollInput {
    fiscalReq: number | null;
    hasFiscalReceipt: boolean;
    startedAt: number;
}

/** The `refetchInterval` answer: poll again in a second, or stop. */
const nextReceiptPoll = ({ fiscalReq, hasFiscalReceipt, startedAt }: NextReceiptPollInput): number | false =>
    shouldPollOrder({ fiscalReq, hasFiscalReceipt, elapsedMs: Date.now() - startedAt })
        ? RECEIPT_POLL_INTERVAL_MS
        : false;

/**
 * Re-reads the order on the success screen while its receipt is in flight
 * (SWRN-2271).
 *
 * On CT5 the receipt is written a few seconds after status 3, and WaitScreen
 * leaves on the first status 3 — so without this the screen keeps the snapshot
 * that has no receipt yet. Each response goes into the store, which is what the
 * screen reads; the query cache is only the transport.
 *
 * A failed poll is skipped, never routed to the error screen: the customer has
 * already paid and been served. Focus and reconnect refetches are off so nothing
 * is requested once the interval has stopped.
 */
export const useReceiptPolling = (): ReceiptPolling => {
    const order = useAppStore((s) => s.order);
    const setOrder = useAppStore((s) => s.setOrder);
    const { showReceiptGlobal: hasFiscalReceipt } = useVmFeatures();
    const startedAt = useRef(Date.now());
    // Decided once, on the order WaitScreen handed over: an order that arrives
    // settled (CT1, Portmone) must not cost a single extra request.
    const [wasInFlight] = useState(
        () =>
            Boolean(order.id) &&
            shouldPollOrder({ fiscalReq: order.fiscal_req, hasFiscalReceipt, elapsedMs: 0 }),
    );

    const { data, error } = useQuery({
        queryKey: ORDER_QUERY_KEYS.receiptPoll(order.id),
        queryFn: () => getOrderInfo(String(order.id)),
        enabled: wasInFlight,
        refetchInterval: (query) =>
            nextReceiptPoll({
                fiscalReq: query.state.data?.fiscal_req ?? order.fiscal_req,
                hasFiscalReceipt,
                startedAt: startedAt.current,
            }),
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        gcTime: 0,
    });

    useEffect(() => {
        if (data) setOrder(data);
    }, [data]);

    useEffect(() => {
        if (error) debug('receipt poll failed, next tick retries', 'warn', error);
    }, [error]);

    return { wasInFlight };
};
